<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, getCurrentInstance, onBeforeMount, onMounted, onUnmounted, ref } from 'vue';

import type { Selection } from '@/types/configuration';
import type { Product } from '@/types/products';

import Carousel from '@/components/carousel/Carousel.vue';
import TheCard from '@/components/mode-list/TheCard.vue';
import { useEditor } from '@/composables/useEditor';
import { i18n } from '@/shared-instance';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { useProductsStore } from '@/stores/useProductsStore';

const props = defineProps<{
  selection?: string;
}>();

const { configToFilter } = useEditor();
const configurationStore = useGlobalConfigurationStore();
const { globalConfiguration } = storeToRefs(configurationStore);
const productsStore = useProductsStore();

const prefixUrl = computed(() => globalConfiguration.value?.prefixUrl ?? null);

const selection = ref<Selection>();
const products = ref<Product[]>([]);

i18n.install(getCurrentInstance()!.appContext.app);

const desktopWidth = ref(window.innerWidth);
const desktopViewport = computed(() => desktopWidth.value >= 768);

const showFavorites = computed(() => globalConfiguration.value?.options?.hasFavorites !== false);

function handleResize() {
  desktopWidth.value = window.innerWidth;
}

onBeforeMount(async () => {
  if (props.selection == null) {
    throw new Error('No config provided for carousel.');
  }
  selection.value = JSON.parse(props.selection);
  if (!globalConfiguration.value || !globalConfiguration.value.apikey)
    throw new Error('No config provided for carousel.');
});

onMounted(async () => {
  if (selection.value == null) {
    throw new Error('Carousel config is missing');
  }
  const result = await productsStore.getProductsList({
    size: 100,
    metrics: [
      {
        type: 'taxonomyCount',
        taxonomyTypes: [
          'TYPES',
          'THEMES',
          'PLACES',
        ],
      },
    ],
    properties: [{ type: 'rating' }, { type: 'nextSessionDate' }, { type: 'product_enrichment_by_distributor' }],
    filters: configToFilter(selection.value),
    sorts: globalConfiguration.value?.apikey === 'YoubookFFVyBGWEQKbWKTJwTArIg9YcAqVEXaSohwM6LZ4FPREkHZg3BWKAB9hm0' ? [{ type: 'random' }] : [{ type: 'next_session' }],
    selections: [],
  });
  products.value = productsStore.formatToProducts(result.data);
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<template>
  <Carousel
    style="min-height: 450px;" :items="products" :min-item-width="270"
    :max-item-width="270"
  >
    <template #item="{ item }">
      <TheCard
        style="height: 99%"
        :product="item"
        :responsive-row="false"
        :prefix-url="prefixUrl"
        :with-slider="true"
        :show-favorite="showFavorites"
        :has-large-viewport="desktopViewport"
      />
    </template>
  </Carousel>
</template>
